import React, { useState, useEffect } from 'react';
import { Save, Loader2, CheckCircle, AlertCircle, Mail, Send, XCircle, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAdminEmailConfig, useEmailHealth, useEmailLogs } from '../../hooks/useAdminEmail';

const PER_PAGE = 50;

const EMAIL_TEMPLATES = [
  { name: 'signup', type: 'Auth', description: 'Confirmação de conta' },
  { name: 'recovery', type: 'Auth', description: 'Redefinição de senha' },
  { name: 'magiclink', type: 'Auth', description: 'Link de acesso' },
  { name: 'email_change', type: 'Auth', description: 'Confirmação de novo email' },
  { name: 'welcome', type: 'Transacional', description: 'Boas-vindas ao OneForm' },
  { name: 'reset-password', type: 'Transacional', description: 'Redefinição de senha (custom)' },
  { name: 'form-response', type: 'Transacional', description: 'Nova resposta de formulário' },
  { name: 'form-published', type: 'Transacional', description: 'Formulário publicado' },
];

const STATUS_TABS = [
  { label: 'Todos', value: null },
  { label: 'Enviados', value: 'sent' },
  { label: 'Falhas', value: 'failed' },
];

const statusBadge = (status: string) => {
  if (status === 'sent') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400">Enviado</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400">Falha</span>;
};

const successRateColor = (rate: number) => {
  if (rate >= 95) return 'text-green-400';
  if (rate >= 80) return 'text-yellow-400';
  return 'text-red-400';
};

export const AdminEmail: React.FC = () => {
  const { config, loading: configLoading, saving, save, setConfig } = useAdminEmailConfig();
  const { health, loading: healthLoading } = useEmailHealth();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [templateFilter, setTemplateFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [saveResult, setSaveResult] = useState<'success' | 'error' | null>(null);

  const { logs, total, loading: logsLoading } = useEmailLogs({ template: templateFilter, status: statusFilter, page });
  const totalPages = Math.ceil(total / PER_PAGE);

  useEffect(() => { setPage(1); }, [statusFilter, templateFilter]);

  const handleSave = async () => {
    const { error } = await save(config);
    setSaveResult(error ? 'error' : 'success');
    setTimeout(() => setSaveResult(null), 3000);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Email Transacional</h1>

      {/* Config + Provider Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Config Section */}
        <div className="lg:col-span-2 bg-neutral-900/50 border border-white/10 rounded-xl p-5">
          <h2 className="text-sm font-medium text-neutral-400 mb-4">Configuração de Envio</h2>
          {configLoading ? (
            <div className="animate-pulse h-32" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-neutral-500">Nome do Remetente</label>
                <input type="text" value={config.from_name}
                  onChange={e => setConfig({ ...config, from_name: e.target.value })}
                  className="w-full mt-1 bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="text-xs text-neutral-500">Email do Remetente</label>
                <input type="email" value={config.from_email}
                  onChange={e => setConfig({ ...config, from_email: e.target.value })}
                  className="w-full mt-1 bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="text-xs text-neutral-500">Site URL</label>
                <input type="url" value={config.site_url}
                  onChange={e => setConfig({ ...config, site_url: e.target.value })}
                  className="w-full mt-1 bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="text-xs text-neutral-500">Logo URL</label>
                <input type="url" value={config.logo_url}
                  onChange={e => setConfig({ ...config, logo_url: e.target.value })}
                  className="w-full mt-1 bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 mt-4">
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-lg text-sm font-medium disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
            {saveResult === 'success' && <span className="flex items-center gap-1 text-sm text-green-400"><CheckCircle className="w-4 h-4" /> Salvo</span>}
            {saveResult === 'error' && <span className="flex items-center gap-1 text-sm text-red-400"><AlertCircle className="w-4 h-4" /> Erro</span>}
          </div>
        </div>

        {/* Provider Section */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-5">
          <h2 className="text-sm font-medium text-neutral-400 mb-4">Provedor</h2>
          <div className="flex items-center gap-3 mb-3">
            <Mail className="w-8 h-8 text-blue-400" />
            <div>
              <p className="text-white font-medium">Resend</p>
              <p className="text-xs text-neutral-500">API transacional</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${health && health.total_all_time > 0 ? 'bg-green-400' : 'bg-yellow-400'}`} />
            <span className="text-xs text-neutral-400">
              {health && health.total_all_time > 0 ? 'Operacional' : 'Aguardando primeiro envio'}
            </span>
          </div>
        </div>
      </div>

      {/* Health KPIs */}
      {!healthLoading && health && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Send className="w-4 h-4 text-green-400" />
              <span className="text-xs text-neutral-500 uppercase tracking-wide">Enviados / 24h</span>
            </div>
            <p className="text-2xl font-bold text-green-400">{health.sent_24h}</p>
          </div>
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-4 h-4 text-red-400" />
              <span className="text-xs text-neutral-500 uppercase tracking-wide">Falhas / 24h</span>
            </div>
            <p className={`text-2xl font-bold ${health.failed_24h > 0 ? 'text-red-400' : 'text-green-400'}`}>{health.failed_24h}</p>
          </div>
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Send className="w-4 h-4 text-blue-400" />
              <span className="text-xs text-neutral-500 uppercase tracking-wide">Enviados / 7d</span>
            </div>
            <p className="text-2xl font-bold text-blue-400">{health.sent_7d}</p>
          </div>
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-neutral-500 uppercase tracking-wide">Success Rate</span>
            </div>
            <p className={`text-2xl font-bold ${successRateColor(health.success_rate)}`}>{health.success_rate}%</p>
          </div>
        </div>
      )}

      {/* Templates List */}
      <div tabIndex={0} className="bg-neutral-900/50 border border-white/10 rounded-xl overflow-x-auto mb-8">
        <div className="p-4 border-b border-white/10">
          <h2 className="text-sm font-medium text-neutral-400">Templates Disponíveis</h2>
        </div>
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="border-b border-white/10 text-neutral-400 text-left">
              <th className="p-3 pl-4">Template</th>
              <th className="p-3">Tipo</th>
              <th className="p-3">Descrição</th>
            </tr>
          </thead>
          <tbody>
            {EMAIL_TEMPLATES.map(t => (
              <tr key={t.name} className="border-b border-white/5">
                <td className="p-3 pl-4 text-white font-mono text-xs">{t.name}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${t.type === 'Auth' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>
                    {t.type}
                  </span>
                </td>
                <td className="p-3 text-neutral-400">{t.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Logs Section */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <h2 className="text-sm font-medium text-neutral-400">Logs de Envio</h2>
        <div className="flex gap-1 ml-auto overflow-x-auto whitespace-nowrap">
          {STATUS_TABS.map(tab => (
            <button key={tab.label} onClick={() => setStatusFilter(tab.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                statusFilter === tab.value ? 'bg-white/10 text-white' : 'text-neutral-400 hover:text-white hover:bg-white/5'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
        <select value={templateFilter || ''} onChange={e => setTemplateFilter(e.target.value || null)}
          className="bg-neutral-900 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white">
          <option value="">Todos templates</option>
          {EMAIL_TEMPLATES.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
        </select>
      </div>

      <div tabIndex={0} className="bg-neutral-900/50 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-white/10 text-neutral-400 text-left">
              <th className="p-4">Destinatário</th>
              <th className="p-4">Template</th>
              <th className="p-4">Status</th>
              <th className="p-4">Erro</th>
              <th className="p-4">Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {logsLoading ? (
              <tr><td colSpan={5} className="p-8 text-center">
                <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
              </td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-neutral-500">Nenhum log encontrado</td></tr>
            ) : logs.map(log => (
              <tr key={log.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="p-4 text-neutral-400 font-mono text-xs">{log.to_email_masked}</td>
                <td className="p-4 text-white text-xs">{log.template}</td>
                <td className="p-4">{statusBadge(log.status)}</td>
                <td className="p-4 text-red-400 text-xs truncate max-w-50">{log.error_message || '—'}</td>
                <td className="p-4 text-neutral-500 text-xs">{new Date(log.created_at).toLocaleString('pt-BR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between mt-4 text-sm text-neutral-400">
          <span>Mostrando {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} de {total}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-1 rounded hover:bg-white/10 disabled:opacity-30">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2">{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="p-1 rounded hover:bg-white/10 disabled:opacity-30">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
